import React, { useRef, useCallback } from 'react';
import { Home, Heart, Plus, MessageSquare, User, Sparkles, LogOut, LayoutDashboard, Settings, ShieldCheck, X, ChevronLeft, Play, Bookmark } from 'lucide-react';
import { isAdminProfile, isSuperAdminProfile } from './Components.js';

export const MobileBottomNav = ({
  currentView,
  isLoggedIn,
  notificationCount,
  onNavigate,
  onPublish,
  onMessages,
  onProfile
}) => (
  <nav
    className="yugui-shell-tabbar yugui-mobile-tabbar md:hidden fixed bottom-0 left-0 right-0 bg-stone-950/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom"
    aria-label="主导航"
  >
    <div className="flex items-center justify-around h-14 px-1">
      <button
        type="button"
        aria-label="发现"
        aria-current={currentView === 'home' ? 'page' : undefined}
        onClick={() => onNavigate('home')}
        className={`flex flex-col items-center justify-center flex-1 py-1 ${currentView === 'home' ? 'text-amber-500' : 'text-white/45'}`}
      >
        <Home size={22} strokeWidth={currentView === 'home' ? 2.5 : 2} />
        <span className="text-[10px] font-bold mt-0.5">发现</span>
      </button>
      <button
        type="button"
        aria-label="领养大厅"
        aria-current={currentView === 'adopt' ? 'page' : undefined}
        onClick={() => onNavigate('adopt')}
        className={`flex flex-col items-center justify-center flex-1 py-1 ${currentView === 'adopt' ? 'text-amber-500' : 'text-white/45'}`}
      >
        <Heart size={22} strokeWidth={currentView === 'adopt' ? 2.5 : 2} />
        <span className="text-[10px] font-bold mt-0.5">领养</span>
      </button>
      <button
        type="button"
        aria-label="发布内容"
        onClick={onPublish}
        className="flex flex-col items-center justify-center -mt-4"
      >
        <span className="w-12 h-12 rounded-full bg-amber-500 text-black flex items-center justify-center shadow-lg shadow-amber-500/30 active:scale-95 transition-transform">
          <Plus size={26} strokeWidth={3} />
        </span>
        <span className="text-[10px] font-bold mt-1 text-amber-500">发布</span>
      </button>
      <button
        type="button"
        aria-label="消息通知"
        onClick={onMessages}
        className="flex flex-col items-center justify-center flex-1 py-1 text-white/45 relative"
      >
        <MessageSquare size={22} />
        {notificationCount > 0 && (
          <span className="absolute top-0 right-[calc(50%-18px)] w-2 h-2 bg-amber-500 rounded-full" />
        )}
        <span className="text-[10px] font-bold mt-0.5">消息</span>
      </button>
      <button
        type="button"
        aria-label="我的"
        aria-current={currentView === 'profile' || currentView === 'user' ? 'page' : undefined}
        onClick={onProfile}
        className={`flex flex-col items-center justify-center flex-1 py-1 ${currentView === 'profile' || currentView === 'user' ? 'text-amber-500' : 'text-white/45'}`}
      >
        <User size={22} strokeWidth={currentView === 'profile' || currentView === 'user' ? 2.5 : 2} />
        <span className="text-[10px] font-bold mt-0.5">我的</span>
      </button>
    </div>
  </nav>
);

export const MobileUserSheet = ({
  userProfile,
  onClose,
  onProfile,
  onFeedback,
  onAdmin,
  onLogout,
  onConvention
}) => (
  <div className="yugui-mobile-only-flex md:hidden fixed inset-0 z-[6500] flex flex-col justify-end" role="dialog" aria-modal="true" aria-label="用户菜单">
    <button type="button" className="flex-1 bg-black/60" onClick={onClose} aria-label="关闭菜单" />
    <div className="bg-zinc-900 border-t border-white/10 rounded-t-[2rem] p-6 safe-area-bottom animate-slide-up">
      <div className="flex items-center gap-4 mb-6">
        <img src={userProfile.avatar} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-amber-500/30" />
        <div>
          <p className="font-black text-lg">{userProfile.name}</p>
          <p className="text-xs text-white/40 font-mono">ID: {userProfile.uid}</p>
        </div>
        <button type="button" onClick={onClose} className="ml-auto p-2 rounded-full bg-white/5" aria-label="关闭">
          <X size={20} />
        </button>
      </div>
      <div className="space-y-2">
        <button type="button" onClick={onProfile} className="w-full px-4 py-3.5 rounded-xl bg-white/5 text-left text-sm font-bold flex items-center gap-3">
          <User size={18} className="text-amber-500" /> 个人主页
        </button>
        <button type="button" onClick={onConvention} className="w-full px-4 py-3.5 rounded-xl bg-white/5 text-left text-sm font-bold flex items-center gap-3">
          <ShieldCheck size={18} className="text-amber-500" /> 平台公约
        </button>
        <button type="button" onClick={onFeedback} className="w-full px-4 py-3.5 rounded-xl bg-white/5 text-left text-sm font-bold flex items-center gap-3">
          <MessageSquare size={18} className="text-amber-500" /> 官方反馈
        </button>
        {isAdminProfile(userProfile) && (
          <button type="button" onClick={onAdmin} className="w-full px-4 py-3.5 rounded-xl bg-amber-500/10 text-left text-sm font-black text-amber-500 flex items-center gap-3">
            <LayoutDashboard size={18} /> {isSuperAdminProfile(userProfile) ? '超级管理后台' : '管理后台'}
          </button>
        )}
        <button type="button" onClick={onLogout} className="w-full px-4 py-3.5 rounded-xl bg-red-500/10 text-left text-sm font-black text-red-400 flex items-center gap-3">
          <LogOut size={18} /> 安全退出
        </button>
      </div>
    </div>
  </div>
);

export const FeedCaption = ({ post, authorLabel, expanded, onToggleExpand, onAuthorClick, authorAvatar }) => {
  const title = String(post.title || '').trim();
  const content = String(post.content || '').trim();
  const bodyText = content || title;
  const showTitleLine = title && title !== content;
  const needsExpand = bodyText.length > 72;
  const name = authorLabel || post.user || post.uid || '用户';

  return (
    <div className="absolute bottom-0 left-0 w-full p-4 md:p-8 pb-20 md:pb-12 pointer-events-none z-10 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
      <button
        type="button"
        onClick={onAuthorClick}
        className="flex items-center gap-2.5 mb-2 pointer-events-auto text-left max-w-[70%]"
        aria-label={`查看 ${name} 的主页`}
      >
        <img
          src={authorAvatar}
          alt=""
          className="w-10 h-10 rounded-full object-cover border-2 border-white/40 shrink-0"
        />
        <span className="font-black text-base md:text-lg drop-shadow-md truncate">@{name}</span>
      </button>
      <div className="pointer-events-auto max-w-[85%] md:max-w-[75%]">
        {showTitleLine && (
          <p className="text-sm md:text-base font-black text-white mb-1 drop-shadow-md line-clamp-1">{title}</p>
        )}
        {bodyText && (
          <p className={`text-sm text-white/90 leading-relaxed font-medium drop-shadow-md ${expanded ? '' : 'line-clamp-2'}`}>
            {bodyText}
          </p>
        )}
        {needsExpand && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="text-xs font-bold text-amber-400 mt-1 drop-shadow-md"
          >
            {expanded ? '收起' : '展开全文'}
          </button>
        )}
      </div>
    </div>
  );
};

/** 发现页全屏视频：重登后须 muted + autoPlay，并在 canplay 时重试播放 */
export const FeedVideo = ({ src, soundOn, className }) => {
  const ref = useRef(null);
  const tryPlay = useCallback(() => {
    const video = ref.current;
    if (!video) return;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, []);

  return (
    <video
      ref={ref}
      data-feed-video
      src={src}
      className={className}
      loop
      muted={!soundOn}
      autoPlay
      playsInline
      preload="auto"
      onLoadedData={tryPlay}
      onCanPlay={tryPlay}
    />
  );
};

export const PROFILE_TAB_GROUPS = [
  {
    id: 'create',
    label: '创作',
    tabs: [
      { id: 'works', label: '我的作品' },
      { id: 'adopts', label: '我的领养' },
      { id: 'drafts', label: '草稿箱' }
    ]
  },
  {
    id: 'saved',
    label: '收藏与喜欢',
    tabs: [
      { id: 'likes', label: '我喜欢的' },
      { id: 'bookmarks', label: '我的收藏' }
    ]
  },
  {
    id: 'social',
    label: '社交',
    tabs: [{ id: 'following', label: '关注的人' }]
  }
];

export const profileTabGroupOf = (tabId) => {
  const hit = PROFILE_TAB_GROUPS.find((g) => g.tabs.some((t) => t.id === tabId));
  return hit?.id || 'create';
};

export const ProfileTabGroups = ({ activeTab, counts, onTabChange }) => {
  const activeGroup = profileTabGroupOf(activeTab);
  const currentGroup = PROFILE_TAB_GROUPS.find((g) => g.id === activeGroup) || PROFILE_TAB_GROUPS[0];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {PROFILE_TAB_GROUPS.map((group) => {
          const groupCount = group.tabs.reduce((sum, t) => sum + (counts[t.id] || 0), 0);
          const selected = group.id === activeGroup;
          return (
            <button
              key={group.id}
              type="button"
              onClick={() => onTabChange(group.tabs[0].id)}
              className={`px-4 py-2 rounded-full text-xs font-black border transition-all ${
                selected
                  ? 'bg-amber-500 text-black border-amber-500 shadow-lg shadow-amber-500/20'
                  : 'bg-white/5 text-white/55 border-white/10 hover:border-white/25 hover:text-white'
              }`}
            >
              {group.label}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${selected ? 'bg-black/15' : 'bg-white/10'}`}>
                {groupCount}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 p-2 rounded-2xl bg-white/[0.04] border border-white/10">
        {currentGroup.tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => onTabChange(tab.id)}
            className={`px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
              activeTab === tab.id
                ? 'bg-amber-500/15 text-amber-400 border border-amber-500/35'
                : 'text-white/45 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            {tab.label}
            <span className="ml-1 text-[10px] opacity-70">({counts[tab.id] || 0})</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export const ProfileWorkTile = ({ mediaUrl, isVideo, onClick, footer, className = '' }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    className={`aspect-[3/4] rounded-2xl overflow-hidden relative cursor-pointer group shadow-xl border border-white/10 bg-zinc-900 hover:border-amber-500/40 hover:shadow-amber-500/10 transition-all ${className}`}
  >
    {isVideo && mediaUrl ? (
      <>
        <video src={mediaUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/40 transition-colors">
          <div className="w-11 h-11 rounded-full bg-black/60 border border-white/25 flex items-center justify-center shadow-lg">
            <Play size={20} className="text-white ml-0.5" fill="white" />
          </div>
        </div>
      </>
    ) : mediaUrl ? (
      <img
        src={mediaUrl}
        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
        alt=""
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center text-white/30 text-xs font-bold">无封面</div>
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent pointer-events-none" />
    {footer}
  </div>
);

export const FeedLoadingState = ({ error, onRetry }) => {
  if (error) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-white/50 px-8">
        <p className="font-black text-base mb-2 text-red-400/90">加载失败</p>
        <p className="text-xs text-center mb-6 text-white/40">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="px-6 py-2.5 rounded-full bg-amber-500 text-black text-sm font-black"
        >
          重试
        </button>
      </div>
    );
  }
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-4 px-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="w-full max-w-[500px] h-24 rounded-2xl bg-white/5 animate-pulse border border-white/5" />
      ))}
      <p className="text-xs text-white/30 font-bold mt-2">正在加载内容…</p>
    </div>
  );
};

export const SyncStatusBanner = ({ queueLength, isSyncing }) => {
  if (!queueLength && !isSyncing) return null;
  return (
    <div className="yugui-mobile-only md:hidden absolute top-0 left-0 right-0 z-20 px-3 py-1.5 bg-amber-500/15 border-b border-amber-500/25 text-center">
      <p className="text-[11px] font-bold text-amber-400">
        {isSyncing ? '正在同步到云端…' : `有 ${queueLength} 项待同步，联网后自动上传`}
      </p>
    </div>
  );
};

export const AdoptFilterBar = ({ filterType, filterLocation, onTypeChange, onLocationChange }) => (
  <div className="flex flex-wrap gap-2 mb-4 md:mb-6">
    <div className="flex gap-1.5 overflow-x-auto no-scrollbar w-full pb-1">
      {[
        { id: 'all', label: '全部' },
        { id: '猫', label: '猫咪' },
        { id: '狗', label: '狗狗' },
        { id: '其他', label: '其他' }
      ].map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onTypeChange(t.id)}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
            filterType === t.id
              ? 'bg-amber-500 text-black border-amber-500'
              : 'bg-white/5 text-white/60 border-white/10'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
    <div className="flex gap-1.5">
      {[
        { id: 'all', label: '全国' },
        { id: 'sameCity', label: '附近优先' }
      ].map((l) => (
        <button
          key={l.id}
          type="button"
          onClick={() => onLocationChange(l.id)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
            filterLocation === l.id
              ? 'bg-amber-500/20 text-amber-400 border-amber-500/40'
              : 'bg-white/5 text-white/60 border-white/10'
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  </div>
);
